
/**
 * Parses a string containing reps and returns the highest number.
 * Handles ranges, time formats, and special keywords.
 * @param {string} repsString - The string to parse (e.g., "8-12", "45 seconds", "AMRAP").
 * @returns {{value: number, isTime: boolean}} - The parsed value and a flag indicating if it's time-based.
 */
const parseReps = (repsString = '') => {
    const s = String(repsString).toLowerCase();

    // Handle AMRAP/effort-based keywords (treat as 60s timed work)
    if (
        s.includes('amrap') ||
        s.includes('to failure') ||
        s.includes('as many as possible') ||
        s.includes('as many reps as possible') ||
        s.includes('max reps')
    ) {
        return { value: 60, isTime: true }; // 60 seconds
    }

    // Handle time formats
    if (s.includes('second')) {
        const numbers = s.match(/\d+/g);
        return { value: numbers ? Math.max(...numbers.map(Number)) : 0, isTime: true };
    }
    if (s.includes('minute')) {
        const numbers = s.match(/\d+/g);
        return { value: numbers ? Math.max(...numbers.map(Number)) * 60 : 0, isTime: true };
    }

    // Handle numeric ranges or single numbers
    const numbers = s.match(/\d+/g);
    if (numbers) {
        return { value: Math.max(...numbers.map(Number)), isTime: false };
    }

    return { value: 0, isTime: false };
};

/**
 * Parses a rest string (e.g., "60 seconds", "2 minutes") and returns seconds.
 * @param {string} restString - The string to parse.
 * @returns {number} - The rest duration in seconds.
 */
const parseRest = (restValue = '') => {
    if (typeof restValue === 'number' && Number.isFinite(restValue)) {
        return Math.max(0, Math.round(restValue));
    }

    const s = String(restValue).toLowerCase();
    const numbers = s.match(/\d+/g);
    if (!numbers) return 60; // Default rest

    const value = Math.max(...numbers.map(Number));
    if (s.includes('minute')) {
        return value * 60;
    }
    return value; // Assume seconds if not specified
};

const normalizeExerciseName = (value = '') => String(value || '').trim().toLowerCase();

const toFiniteNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const matchesPhaseIdentity = (savedPhase, candidatePhase) => {
    if (!savedPhase || !candidatePhase || savedPhase.type !== candidatePhase.type) {
        return false;
    }

    if (savedPhase.type === 'work') {
        const savedExerciseIndex = toFiniteNumber(savedPhase.exerciseIndex);
        const candidateExerciseIndex = toFiniteNumber(candidatePhase.exerciseIndex);
        const savedSetNumber = toFiniteNumber(savedPhase.setNumber);
        const candidateSetNumber = toFiniteNumber(candidatePhase.setNumber);

        if (savedExerciseIndex !== null && candidateExerciseIndex !== null) {
            return savedExerciseIndex === candidateExerciseIndex
                && (savedSetNumber === null || candidateSetNumber === null || savedSetNumber === candidateSetNumber);
        }

        return normalizeExerciseName(savedPhase.exerciseName) === normalizeExerciseName(candidatePhase.exerciseName)
            && (savedSetNumber === null || candidateSetNumber === null || savedSetNumber === candidateSetNumber);
    }

    const savedNextExerciseIndex = toFiniteNumber(savedPhase.nextExerciseIndex);
    const candidateNextExerciseIndex = toFiniteNumber(candidatePhase.nextExerciseIndex);
    const savedNextSetNumber = toFiniteNumber(savedPhase.nextSetNumber);
    const candidateNextSetNumber = toFiniteNumber(candidatePhase.nextSetNumber);

    if (savedNextExerciseIndex !== null && candidateNextExerciseIndex !== null) {
        return savedNextExerciseIndex === candidateNextExerciseIndex
            && (savedNextSetNumber === null || candidateNextSetNumber === null || savedNextSetNumber === candidateNextSetNumber);
    }

    return normalizeExerciseName(savedPhase.nextExerciseName) === normalizeExerciseName(candidatePhase.nextExerciseName)
        && (savedNextSetNumber === null || candidateNextSetNumber === null || savedNextSetNumber === candidateNextSetNumber);
};

const findMatchingPhaseIndex = ({ savedPhase, savedPhaseIndex, freshPhases }) => {
    if (!savedPhase || !Array.isArray(freshPhases) || freshPhases.length === 0) {
        return 0;
    }

    const exactMatchIndex = freshPhases.findIndex((phase) => matchesPhaseIdentity(savedPhase, phase));
    if (exactMatchIndex !== -1) {
        return exactMatchIndex;
    }

    const fallbackIndex = Number.isInteger(savedPhaseIndex) ? savedPhaseIndex : 0;
    return Math.min(Math.max(fallbackIndex, 0), freshPhases.length - 1);
};

/**
 * Generates a flat sequence of work and rest phases for a given quest.
 * @param {object} quest - The quest object from the database.
 * @returns {Array<object>} - An array of phase objects.
 */
export const generatePhaseSequence = (quest) => {
    if (!quest?.detailedDescription?.exercises) return [];

    const phases = [];
    const exercises = quest.detailedDescription.exercises;

    const hasUnilateralMarker = (repsText = '') => {
        const t = String(repsText).toLowerCase();
        const markers = ['per leg', 'each leg', 'per arm', 'each arm', 'per side', 'each side'];
        return markers.some(m => t.includes(m));
    };

    for (let i = 0; i < exercises.length; i++) {
        const exercise = exercises[i];
        const setsRaw = exercise.sets;
        const sets = Number.isFinite(setsRaw) ? Math.max(1, Math.round(setsRaw)) : parseInt(String(setsRaw).match(/\d+/g)?.[0] || '1', 10);
        const { value: repsValue, isTime: repsIsTime } = parseReps(exercise.reps);
        const restDuration = parseRest(exercise.rest);
        const unilateral = hasUnilateralMarker(exercise.reps);
        const repsText = String(exercise.reps || '').toLowerCase();
        const mentionsTotal = repsText.includes('total');

        // For Mindfulness quests, it's one continuous session
        if (quest.type === 'Mindfulness' && repsIsTime) {
            let duration = repsValue;
            if (unilateral && !mentionsTotal) {
                duration = duration * 2;
            }
            if (!Number.isFinite(duration) || duration <= 0) {
                duration = 60;
            }
            phases.push({
                type: 'work',
                exerciseIndex: i,
                duration,
                exerciseName: exercise.name,
                exerciseImage: exercise.imageUrl,
                exerciseVideo: exercise.videoUrl || '',
                exercisePoster: exercise.posterUrl || '',
                instructions: exercise.instructions,
                setNumber: 1,
                totalSets: 1,
            });
            continue; // Move to next exercise (or finish)
        }

        for (let set = 1; set <= sets; set++) {
            // Add work phase
            let workDuration = repsIsTime ? repsValue : repsValue * 5; // 5 seconds per rep if not time-based
            if (unilateral && !mentionsTotal) {
                workDuration = workDuration * 2;
            }
            if (!Number.isFinite(workDuration) || workDuration <= 0) {
                workDuration = 60; // Default fallback
            }
            phases.push({
                type: 'work',
                exerciseIndex: i,
                duration: workDuration,
                exerciseName: exercise.name,
                exerciseImage: exercise.imageUrl,
                exerciseVideo: exercise.videoUrl || '',
                exercisePoster: exercise.posterUrl || '',
                instructions: exercise.instructions,
                setNumber: set,
                totalSets: sets,
                sets,
                reps: exercise.reps,
                rest: restDuration,
                suggestedWeight: exercise.suggestedWeight
            });

            // Add rest phase if it's not the last set of the last exercise
            if (set < sets || i < exercises.length - 1) {
                if (restDuration > 0) {
                    const nextExerciseIndex = set < sets ? i : i + 1;
                    const nextExercise = exercises[nextExerciseIndex] || {};
                    const nextSetNumber = set < sets ? set + 1 : 1;

                    phases.push({
                        type: 'rest',
                        duration: restDuration,
                        exerciseName: 'Rest',
                        exerciseImage: '', // No image for rest
                        exerciseVideo: '',
                        exercisePoster: '',
                        instructions: 'Prepare for the next set.',
                        setNumber: set,
                        totalSets: sets,
                        nextExerciseIndex,
                        nextExerciseName: nextExercise.name || '',
                        nextSetNumber,
                        nextReps: nextExercise.reps || '',
                    });
                }
            }
        }
    }

    return phases;
};

export const rehydrateTimerStateWithFreshPhases = (savedState, quest) => {
    const freshPhases = generatePhaseSequence(quest);
    if (!savedState || freshPhases.length === 0) {
        return savedState;
    }

    const savedPhaseIndex = Number.isInteger(savedState.currentPhaseIndex) ? savedState.currentPhaseIndex : 0;
    const savedPhase = Array.isArray(savedState.phases) ? savedState.phases[savedPhaseIndex] : null;
    const currentPhaseIndex = findMatchingPhaseIndex({ savedPhase, savedPhaseIndex, freshPhases });
    const currentPhaseDuration = freshPhases[currentPhaseIndex]?.duration || 0;
    const savedTimeRemaining = toFiniteNumber(savedState.timeRemaining);

    return {
        ...savedState,
        quest,
        phases: freshPhases,
        currentPhaseIndex,
        timeRemaining: savedTimeRemaining === null
            ? currentPhaseDuration
            : Math.min(Math.max(savedTimeRemaining, 0), currentPhaseDuration),
    };
};

/**
 * Formats seconds into a MM:SS string.
 * @param {number} totalSeconds - The total seconds to format.
 * @returns {string} - The formatted time string.
 */
export const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};
